import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { StackedCell } from '../../../../components/table/StackedCell';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { usePersonNames } from '../../personnel/usePersonNames';
import { applicantDisplayName } from '../../applicants/applicantName';
import {
  APPLICANT_ASSESSMENT_STAGES,
  APPLICANT_HEALTH_PERMISSION,
  type ApplicantScoreWeights,
  type JobPostingListItem,
} from '../../applicants/applicantTypes';
import { deliberationApi } from '../deliberationApi';
import {
  DELIBERATION_ENDORSE_PERMISSION,
  DELIBERATION_READ_PERMISSION,
  APPOINTED_FILTER,
  ENDORSED_FILTER,
  SHORTLISTED_FILTER,
  type DeliberationApplicantRow,
  type DeliberationStageScore,
} from '../deliberationTypes';
import { formatPoints } from '../formatPoints';
import { stageOutcome } from '../stageOutcome';
import { DeliberationApplicantModal } from './DeliberationApplicantModal';
import { ApplicantFilterRail, type ApplicantFilterKey } from './ApplicantFilterRail';
import { EndorsementModal } from './EndorsementModal';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { BadgeCheck, Undo2 } from 'lucide-react';

/**
 * One stage's cell, SPLIT in two by a vertical rule (product calls, 20 Oct 2026): the outcome code on
 * the left — `P` / `F` / `-`, the rail's mapping but PLAIN TEXT, no pill ("no need to badge the left
 * cell") — and the points it earned on the right. The rule is the grid's own `slate-200`: a table is
 * one grid, one tone. Two values in one cell is legal in a panel table; the index surface alone
 * refuses it. The code is `aria-hidden` with its word in an `sr-only` twin. A hidden health stage is
 * the word `Hidden` and nothing else.
 */
function StageScoreCell({ stage }: { stage: DeliberationStageScore | undefined }) {
  /*
    The column is `flush`: the cell has no padding and is the positioning box, so this fills it
    (`absolute inset-0`) and the rule between the halves runs the FULL HEIGHT of the row (product call,
    20 Oct 2026). Each half carries the cell's usual `px-3` itself.
  */
  // "Hidden" is a word, not a figure — it takes the body face.
  if (stage?.isHidden) {
    return (
      <div className="absolute inset-0 flex items-center justify-end px-3">
        <TextCell value="Hidden" />
      </div>
    );
  }

  const outcome = stageOutcome(stage);

  return (
    <div className="absolute inset-0 flex">
      {/* Two EQUAL halves (product call, 20 Oct 2026) — `basis-0` so the content cannot tip the split, and the rule falls mid-cell down the whole column. */}
      <span className="flex min-w-0 flex-1 basis-0 items-center justify-center font-mono text-sm text-slate-500">
        <span aria-hidden="true">{outcome?.code}</span>
        {outcome ? <span className="sr-only">{outcome.label}</span> : null}
      </span>
      {/*
        The rule and the inset sit on an INNER box: on the flex item itself, padding and border are a
        floor `basis-0` cannot go under, so the score half came out ~25px wider than the code half.
      */}
      <span className="flex min-w-0 flex-1 basis-0">
        <span className="flex min-w-0 flex-1 items-center justify-end border-l border-slate-200 px-3">
          <TextCell value={stage?.score != null ? formatPoints(stage.score) : null} mono numeric />
        </span>
      </span>
    </div>
  );
}

/**
 * The SURNAME out of a resolved display name, which the server formats surname first ("Dela Cruz, Juan
 * P."). Null for anything that is not a name — `…` while loading, `Identity erased`, `Name unavailable` —
 * so the step button falls back to Back / Next rather than printing a status as if it were a surname.
 */
function surnameOf(displayName: string): string | null {
  const comma = displayName.indexOf(',');
  return comma > 0 ? displayName.slice(0, comma).trim() : null;
}

/** The band over the stage columns and the total — one declaration for the band AND the columns. */
const SCORE_BAND = 'Score';

/**
 * A posting's applicants for deliberation — the Applicants tab of the deliberation profile. One row
 * per LIVE applicant, and a column per assessment stage holding the points that stage earned, then
 * the total: the Summary's figures, laid side by side so applicants can be weighed against each other.
 *
 * **Read only**: no create, no row menu beyond Other Info, no selection — nothing can be done to a row
 * here. The anchor opens the applicant's deliberation profile in a full-screen modal.
 *
 * **The two HEALTH stages' columns carry `applicants.read-medical`**, and the server leaves their values
 * out without it; the TOTAL then reads `Hidden` on every row, because a total over the visible stages
 * would let a reader subtract their way to the hidden one.
 *
 * Server-side search, sort and paging. **The NAME and the SCORES can be neither searched nor sorted** —
 * the name is in the other database, and the scores are computed per page.
 */
export function DeliberationApplicantsPanel({
  posting,
  weights,
  onChanged,
}: {
  /** The posting — its id, the post the applicant's profile names, and whether it is COMPLETED. */
  posting: Pick<JobPostingListItem, 'id' | 'positionTitle' | 'itemNumber' | 'hasEndorsement' | 'orgUnitId'>;
  /** An endorsement was made or withdrawn — the posting's Completed state moved; the profile refetches it. */
  onChanged: () => void;
  /** Score Points, for the applicant profile's Weight column. Null while loading or unreadable. */
  weights: ApplicantScoreWeights | null;
}) {
  const { hasPermission } = useAuth();
  /** The applicant open in the modal, by ID — so a reload after a save hands it the fresh row. */
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<ApplicantFilterKey>('all');
  /** The rail's two badges, off the server's `shortlist` facet — undefined until the first page. */
  const [counts, setCounts] = useState<Partial<Record<'all' | 'shortlisted' | 'endorsed' | 'appointed', number>>>({});
  /** The endorsement awaiting its confirmation — ONE piece of state, so two dialogs can never stack. */
  const [confirming, setConfirming] = useState<{
    row: DeliberationApplicantRow;
    action: 'endorse' | 'withdraw';
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const toast = useToast();

  async function runEndorsement() {
    if (!confirming) return;

    setRunning(true);
    setActionError(null);

    try {
      // Only WITHDRAW confirms here — Endorse is a form, `<EndorsementModal>`, with its own tick.
      await deliberationApi.withdrawEndorsement(posting.id, confirming.row.applicant.id);
      toast.success('Endorsement withdrawn.');
      setConfirming(null);
      state.reload();
      onChanged();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The endorsement could not be changed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const fetchPage = useCallback(
    async (query: DataTableQuery, signal: AbortSignal) => {
      const page = await deliberationApi.listApplicants(
        posting.id,
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort,
          shortlist:
            filter === 'shortlisted'
              ? SHORTLISTED_FILTER
              : filter === 'endorsed'
                ? ENDORSED_FILTER
                : filter === 'appointment'
                  ? APPOINTED_FILTER
                  : undefined,
        },
        signal,
      );
      // Counted against the same search, whichever entry is open.
      setCounts(Object.fromEntries((page.facets?.shortlist ?? []).map((f) => [f.value, f.count])));
      return { items: page.items, total: page.total };
    },
    [posting.id, filter],
  );

  // Unsorted means the server's order: most recently applied first.
  const state = useDataTable<DeliberationApplicantRow>({ fetchPage, pageSize: 10 });

  const personIds = useMemo(() => state.rows.map((r) => r.applicant.personId), [state.rows]);
  const names = usePersonNames(personIds, deliberationApi.names);

  // The open row, and its neighbours ON THIS PAGE — what Back and Next step to. A row a reload took
  // off the list (un-shortlisted under Short Listed) closes the modal, since there is nothing to show.
  const openedIndex = openedId === null ? -1 : state.rows.findIndex((r) => r.applicant.id === openedId);
  const opened = openedIndex >= 0 ? state.rows[openedIndex] : null;
  const previous = openedIndex > 0 ? state.rows[openedIndex - 1] : null;
  const next = openedIndex >= 0 && openedIndex < state.rows.length - 1 ? state.rows[openedIndex + 1] : null;

  const columns: DataColumn<DeliberationApplicantRow>[] = [
    {
      /*
        RANK, right after the `#` — AUTOMATIC, the place by TOTAL among the posting's live applicants, ties
        sharing a place (product call, 20 Oct 2026), computed by the server across the whole posting. FIRST
        but NOT the click target — the Name is (`anchor`). Not sortable: the sort is the Applicants list's
        allow-list. LEFT-aligned — a place in an order, read beside the name, not a sum. Where the total is
        hidden the rank is too, said in a word rather than a dash, which would read as "not ranked".
      */
      key: 'rank',
      header: 'Rank',
      width: 90,
      render: (r) =>
        r.isTotalHidden ? (
          <TextCell value="Hidden" />
        ) : (
          <TextCell value={r.rank != null ? String(r.rank) : null} mono numeric />
        ),
    },
    {
      /*
        The name over the DATE APPLIED (product call, 20 Oct 2026) — two values in one cell, legal in a
        panel table and refused only on an index surface. Not sortable: the name is in the other
        database, and the date no longer has a header of its own to sort from; the server's order is
        still most recently applied first.
      */
      key: 'name',
      // The row's CLICK TARGET though Rank leads (product call, 20 Oct 2026) — the name identifies the row.
      anchor: true,
      header: 'Name',
      width: 240,
      render: (r) => (
        <StackedCell
          primary={applicantDisplayName(r.applicant.personId, names)}
          // A calendar date, not an instant.
          secondary={formatDateOnly(r.applicant.dateApplied)}
          mono
        />
      ),
    },
    /*
      One column per stage, in the stage order. A HEALTH stage's column is dropped without the medical
      key — `DataColumn` carries no `permission` of its own, so the panel resolves it here, once.
    */
    ...APPLICANT_ASSESSMENT_STAGES.filter((s) => !s.isHealth || hasPermission(APPLICANT_HEALTH_PERMISSION)).map(
      (s): DataColumn<DeliberationApplicantRow> => ({
        key: `score-${s.key}`,
        // The cell draws its own rule the full height of the row, so it takes no padding.
        flush: true,
        header: s.label,
        width: 130,
        align: 'right',
        group: SCORE_BAND,
        render: (r) => <StageScoreCell stage={r.stages.find((x) => x.stage === s.value)} />,
      }),
    ),
    {
      key: 'total',
      header: 'Total',
      width: 110,
      align: 'right',
      group: SCORE_BAND,
      summary: true,
      render: (r) =>
        r.isTotalHidden ? (
          <TextCell value="Hidden" />
        ) : (
          <TextCell value={r.total != null ? formatPoints(r.total) : null} mono numeric />
        ),
    },
  ];

  return (
    /*
      The FILTER RAIL at the panel's left edge (product call, 20 Oct 2026) — All and Short Listed, each
      with its count — the shared `<ProfileNav>` in a `w-56` aside, the shape the survey's Responses
      tab already gives a panel. The table keeps the rest of the width and its own inset.
    */
    <div className="flex h-full min-h-0 min-w-0">
      <ApplicantFilterRail
        id="deliberation-applicants-filter"
        active={filter}
        counts={counts}
        onSelect={(key) => {
          setFilter(key);
          // A narrower list re-reads from its first page.
          state.setPage(1);
        }}
      />

      <div className="flex h-full min-w-0 flex-1 flex-col p-4">
        <DataTablePanel<DeliberationApplicantRow>
          id="deliberation-applicants"
          state={state}
          columns={columns}
          rowKey={(r) => r.applicant.id}
          fill
          // Nothing can be done to a row here — the deliberation is recorded inside the applicant.
          selectable={false}
          searchPlaceholder="Search email, mobile number or remarks"
          onRowOpen={(r) => setOpenedId(r.applicant.id)}
          openPermission={DELIBERATION_READ_PERMISSION}
          otherInfo={(r) => r.applicant}
          /*
            The row menu (product call, 20 Oct 2026): ENDORSE APPOINTMENT for a shortlisted applicant, and
            its undo once endorsed — each under `deliberations.endorse`, hidden without it, disabled for
            state with the reason the server's refusal gives. Both confirm first.
          */
          rowActions={(r) => [
            {
              key: `endorse-${r.applicant.id}`,
              label: 'Endorse Appointment',
              icon: <BadgeCheck className="h-4 w-4" aria-hidden="true" />,
              permission: DELIBERATION_ENDORSE_PERMISSION,
              disabledReason: r.isEndorsed
                ? "Already endorsed for appointment — correct it in the applicant's Endorsement."
                : !r.isShortlisted
                  ? 'Only a shortlisted applicant is endorsed for appointment. Shortlist them in their Decision first.'
                  : posting.hasEndorsement
                    ? 'Another applicant on this posting is endorsed for appointment. Withdraw that endorsement first.'
                    : null,
              onClick: () => {
                setActionError(null);
                setConfirming({ row: r, action: 'endorse' });
              },
            },
            {
              key: `withdraw-${r.applicant.id}`,
              label: 'Withdraw endorsement',
              icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
              permission: DELIBERATION_ENDORSE_PERMISSION,
              disabledReason: r.isEndorsed ? null : 'This applicant is not endorsed for appointment.',
              onClick: () => {
                setActionError(null);
                setConfirming({ row: r, action: 'withdraw' });
              },
            },
          ]}
          emptyMessage={
            filter === 'shortlisted'
              ? 'No applicant is shortlisted yet.'
              : filter === 'endorsed'
                ? 'No applicant is endorsed for appointment yet.'
                : filter === 'appointment'
                  ? "No appointment recorded yet — an endorsed applicant's oath taking or assignment, in their Appointment."
                  : 'No applicant recorded on this posting yet.'
          }
          emptySearchMessage="No applicant matches that search."
        />
      </div>

      {opened ? (
        <DeliberationApplicantModal
          row={opened}
          name={applicantDisplayName(opened.applicant.personId, names)}
          posting={posting}
          weights={weights}
          onPrevious={previous ? () => setOpenedId(previous.applicant.id) : null}
          onNext={next ? () => setOpenedId(next.applicant.id) : null}
          previousSurname={previous ? surnameOf(applicantDisplayName(previous.applicant.personId, names)) : null}
          nextSurname={next ? surnameOf(applicantDisplayName(next.applicant.personId, names)) : null}
          onClose={() => setOpenedId(null)}
          // The modal stays open on its saved Decision; the list behind it refetches the rank, the
          // shortlist and both badges.
          onSaved={() => {
            state.reload();
            onChanged();
          }}
        />
      ) : null}

      {/*
        Endorse / withdraw CONFIRMS — a button that writes, outside a form: `requireConfirm`, no password
        (an ordinary act of the job). The consequence is said before the tick, in the record's own terms.
      */}
      {/* ENDORSE is a FORM (product call, 21 Oct 2026) — who, when, remarks and the document. */}
      {confirming?.action === 'endorse' ? (
        <EndorsementModal
          postingId={posting.id}
          applicantId={confirming.row.applicant.id}
          applicantName={applicantDisplayName(confirming.row.applicant.personId, names)}
          positionTitle={posting.positionTitle}
          onClose={() => setConfirming(null)}
          onSaved={() => {
            setConfirming(null);
            state.reload();
            onChanged();
          }}
        />
      ) : null}

      {confirming?.action === 'withdraw' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setActionError(null);
          }}
          onConfirm={() => void runEndorsement()}
          title="Withdraw endorsement"
          requireConfirm
          loading={running}
          id="deliberation-withdraw-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {`${applicantDisplayName(confirming.row.applicant.personId, names)} is no longer endorsed for appointment. They stay shortlisted, and the endorsement's details and any oath-taking schedule are kept.`}
              </p>
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
